import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, catchError, map, of, shareReplay } from 'rxjs';
import { environment } from 'src/environments/enviroment.prod';

export interface SidenavToggleState {
  visible: boolean;
  open: boolean;
}

/** Registered by the configurator's NavbarComponent while it can lose unsaved work. */
export interface LeaveGuard {
  /** True when leaving now needs the user's confirmation. */
  blocks(): boolean;
  /** Ask the user; navigate to `url` on confirm. */
  confirm(url: string): void;
}

/**
 * The only channel between the per-page NavbarComponent and the app-shell
 * top bar. Every page renders its own <app-navbar>, so only the configurator
 * instance writes here (see NavbarComponent.registerWithShell).
 */
@Injectable({ providedIn: 'root' })
export class ShellContextService {
  private readonly _tabName = new BehaviorSubject<string | null>(null);
  readonly tabName$ = this._tabName.asObservable();

  private readonly _sidenavToggle = new BehaviorSubject<SidenavToggleState>({ visible: false, open: false });
  readonly sidenavToggle$ = this._sidenavToggle.asObservable();

  private readonly _sidenavToggleRequests = new Subject<void>();
  readonly sidenavToggleRequests$ = this._sidenavToggleRequests.asObservable();

  private leaveGuard: LeaveGuard | null = null;
  private readonly productNames = new Map<number, Observable<string | null>>();

  constructor(private http: HttpClient, private router: Router) {}

  setTabName(name: string | null): void {
    if (this._tabName.value !== name) { this._tabName.next(name); }
  }

  setSidenavToggle(state: SidenavToggleState): void {
    const current = this._sidenavToggle.value;
    if (current.visible !== state.visible || current.open !== state.open) {
      this._sidenavToggle.next(state);
    }
  }

  requestSidenavToggle(): void {
    this._sidenavToggleRequests.next();
  }

  setLeaveGuard(guard: LeaveGuard): void {
    this.leaveGuard = guard;
  }

  clearLeaveGuard(guard: LeaveGuard): void {
    if (this.leaveGuard === guard) { this.leaveGuard = null; }
  }

  wouldBlockLeave(): boolean {
    return this.leaveGuard?.blocks() ?? false;
  }

  navigate(url: string): void {
    if (this.leaveGuard?.blocks()) {
      this.leaveGuard.confirm(url);
      return;
    }
    this.router.navigateByUrl(url);
  }

  productName$(id: number): Observable<string | null> {
    let cached = this.productNames.get(id);
    if (!cached) {
      cached = this.http.get<{ data: { name: string } }>(`${environment.apiLink}/final_product/${id}`).pipe(
        map(response => response.data?.name ?? null),
        catchError(() => of(null)),
        shareReplay(1)
      );
      this.productNames.set(id, cached);
    }
    return cached;
  }
}
